import { useCallback, useState } from 'react';

import { MissingHealthcareProviderActiveP1CertificatesFault } from '@dreryk/egabinet-p1-frontend-api';
import {
   ElectronicReferralApprovalResult,
   P1HandshakeFault,
   ReferralVerificationFault,
} from '@dreryk/egabinet-referral-frontend-api';
import {
   InvalidPrivateKeyFault,
   NoValidCertificateFoundFault,
   PrivateKeyNotDecodedFault,
} from '@dreryk/egabinet-truststore-published-language-api';
import { VisitReferralToDiagnosticExaminationApproveCommand } from '@dreryk/egabinet-visit-frontend-api';

import { useCqrsCommand, useDataSyncing } from '@dreryk/command-query-react-common';
import {
   ActionBar,
   ButtonVariant,
   DataSyncWaitingEventInterceptor,
   dispatchSnackbarNegativeEvent,
   usePushBack,
   ValidateFormEventInterceptor,
} from '@dreryk/react-components';
import { useDraftRemoveByTokenCqrsCommand } from '@dreryk/react-draft';

import { PatientNfzDepartmentUpdateShowModalEventInterceptor } from '~libs.desktop.shared.patient';
import { useAccountAuthorizationModal, useCertificateFaultHandlerMap } from '~libs.desktop.shared.account';
import { visitPaths } from '~libs.desktop.shared.paths';

import { getReferralDraftToken } from '../../referral.draft';
import {
   dispatchElectronicReferralCodeSnackbarPositiveEvent,
   getReferralVerificationResultErrorList,
} from '../../shared/common.utils';
import { ElectronicReferralFailureModal } from '../../shared/ElectronicReferralFailureModal';
import { referralTranslationKeys } from '../../translations';
import {
   ReferralToDiagnosticExaminationFormBean,
   ReferralToDiagnosticExaminationFormController,
} from '../ReferralToDiagnosticExaminationForm/ReferralToDiagnosticExaminationForm.types';

import { useReferralToDiagnosticExaminationConfirmAndPrintCommand } from './ReferralToDiagnosticExaminationSendAction.hooks';
import { mapReferralToDiagnosticExaminationFormBeanToReferralCommandDetails } from './ReferralToDiagnosticExaminationSendAction.utils';

type ReferralToDiagnosticExaminationSendActionProps = {
   readonly handleSubmit: ReferralToDiagnosticExaminationFormController['handleSubmit'];
   readonly trigger: ReferralToDiagnosticExaminationFormController['trigger'];
   readonly onSuccess: VoidFunction;
   readonly visitId: number;
   readonly patientId: number;
};

// eslint-disable-next-line max-lines-per-function
export const ElectronicReferralToDiagnosticExaminationSendAction = (
   props: ReferralToDiagnosticExaminationSendActionProps
): JSX.Element => {
   const [referralVerificationErrors, setReferralVerificationErrors] = useState<string[] | undefined>();
   const { openAccountAuthorizationModal, renderAccountAuthorizationnModal } = useAccountAuthorizationModal();
   const push = usePushBack();

   const handleReferralVerificationErrors = useCallback((fault?: ReferralVerificationFault) => {
      setReferralVerificationErrors(fault ? getReferralVerificationResultErrorList(fault) : []);
   }, []);

   const { executeAsync: removeDraft } = useDraftRemoveByTokenCqrsCommand();
   const removeElectronicReferralToDiagnosticExaminationDraft = useCallback(
      async () =>
         removeDraft({ token: getReferralDraftToken('electronicReferralToDiagnosticExamination', props.visitId) }),
      [props.visitId, removeDraft]
   );

   const { executeAsync: sendReferralToDiagnosticExamination } = useCqrsCommand<
      ElectronicReferralApprovalResult,
      ReferralToDiagnosticExaminationFormBean,
      | InvalidPrivateKeyFault
      | NoValidCertificateFoundFault
      | PrivateKeyNotDecodedFault
      | ReferralVerificationFault
      | MissingHealthcareProviderActiveP1CertificatesFault
      | P1HandshakeFault
   >(
      (parameters) => {
         const command = new VisitReferralToDiagnosticExaminationApproveCommand();
         command.visitId = props.visitId;
         command.referralDetails = mapReferralToDiagnosticExaminationFormBeanToReferralCommandDetails(parameters, true);
         return command;
      },
      {
         onSuccess: (result) => {
            dispatchElectronicReferralCodeSnackbarPositiveEvent(result.code);
            props.onSuccess();
            removeElectronicReferralToDiagnosticExaminationDraft();
            push(
               visitPaths.getVisitInterviewPath({
                  params: {
                     visitId: props.visitId,
                  },
               })
            );
         },
         onFault: {
            ...useCertificateFaultHandlerMap({
               onAuthenticationRequired: openAccountAuthorizationModal,
            }),
            'egabinet-referral-frontend-api:ReferralVerificationFault': handleReferralVerificationErrors,
            'egabinet-p1-frontend-api:MissingHealthcareProviderActiveP1CertificatesFault': () =>
               dispatchSnackbarNegativeEvent(referralTranslationKeys.missingHealthProviderCertificates),
            'egabinet-referral-frontend-api:P1HandshakeFault': () => {
               dispatchSnackbarNegativeEvent(referralTranslationKeys.certificateErrorOccurred);
            },
         },
      }
   );
   const {
      executeAsync: confirmAndPrintReferralToDiagnosticExamination,
   } = useReferralToDiagnosticExaminationConfirmAndPrintCommand(props.visitId, async () => {
      props.onSuccess();
      await removeElectronicReferralToDiagnosticExaminationDraft();
   });
   return (
      <>
         <ValidateFormEventInterceptor trigger={props.trigger}>
            <DataSyncWaitingEventInterceptor waitUntil={useDataSyncing()}>
               {(waiting) => (
                  <PatientNfzDepartmentUpdateShowModalEventInterceptor
                     patientId={props.patientId}
                     modalLabel={referralTranslationKeys.missingPatientNFZInfo}
                  >
                     <ActionBar.PrimaryButton
                        variant={ButtonVariant.PRIMARY}
                        onClick={props.handleSubmit(sendReferralToDiagnosticExamination)}
                        label={referralTranslationKeys.sendElectronicReferral}
                        testId={'sendReferral'}
                        disabled={waiting}
                        blockingOperation={referralTranslationKeys.submittingElectronicReferral}
                     />
                  </PatientNfzDepartmentUpdateShowModalEventInterceptor>
               )}
            </DataSyncWaitingEventInterceptor>
         </ValidateFormEventInterceptor>
         {renderAccountAuthorizationnModal({
            onAuthorization: props.handleSubmit(sendReferralToDiagnosticExamination),
         })}
         {referralVerificationErrors ? (
            <ElectronicReferralFailureModal
               onConfirm={props.handleSubmit(confirmAndPrintReferralToDiagnosticExamination)}
               onCancel={() => setReferralVerificationErrors(undefined)}
               errors={referralVerificationErrors}
            />
         ) : null}
      </>
   );
};
